(() => {
  'use strict';
  if (window.DecorMotionStudio) return;

  // These are atmospheric images only. Product artwork and blueprints keep
  // their own layout and interaction behavior.
  const selector = '.studio-decor-disc img, .studio-hero-orbit img, .studio-atmosphere-fallback, .studio-booking-art--custom img';
  const states = new Map();
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const enabled = () => !document.hidden && !reduced.matches
    && document.body.dataset.motionEnabled !== 'false'
    && document.documentElement.dataset.motionEnabled !== 'false';

  // A sampled closed orbit has continuous velocity at the loop boundary.
  // Individual transform properties preserve the existing art's placement,
  // masking and custom-image transforms. Playback runs on the compositor.
  function keyframes(index) {
    const direction = index % 2 ? -1 : 1;
    return Array.from({ length: 97 }, (_, step) => {
      const angle = step / 96 * Math.PI * 2;
      return {
        offset: step / 96,
        translate: `${(Math.sin(angle) * 1.8 * direction).toFixed(5)}% ${(Math.cos(angle) * 2.1).toFixed(5)}%`,
        rotate: `${(Math.sin(angle + .65) * 4 * direction).toFixed(5)}deg`
      };
    });
  }

  function sync(state) {
    const running = enabled() && state.visible && state.image.isConnected;
    if (running && state.animation.playState !== 'running') state.animation.play();
    if (!running && state.animation.playState !== 'paused') state.animation.pause();
    state.image.dataset.decorRunning = String(running);
  }
  function syncAll() { for (const state of states.values()) sync(state); }

  // Observe the actual image, not its section: large decorative discs extend
  // outside the section and can be visible before that section reaches the screen.
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = states.get(entry.target);
      if (!state) continue;
      state.visible = entry.isIntersecting && entry.intersectionRect.width > 0 && entry.intersectionRect.height > 0;
      sync(state);
    }
  }, { threshold: 0 });

  function initialize(scope = document) {
    const images = [...scope.querySelectorAll(selector)];
    if (scope.matches?.(selector)) images.unshift(scope);
    for (const image of images) {
      if (states.has(image)) continue;
      const index = states.size, duration = 14000 + index % 4 * 1700;
      const animation = image.animate(keyframes(index), { duration, iterations: Infinity, easing: 'linear' });
      animation.pause();
      animation.currentTime = duration * ((index * .173 + .11) % 1);
      const state = { image, animation, visible: false };
      states.set(image, state);
      image.dataset.decorMotion = 'true';
      image.dataset.decorRunning = 'false';
      observer.observe(image);
    }
  }

  function remove(scope) {
    for (const [image, state] of states) {
      if (scope !== image && !scope.contains(image)) continue;
      observer.unobserve(image);
      state.animation.cancel();
      delete image.dataset.decorMotion;
      delete image.dataset.decorRunning;
      states.delete(image);
    }
  }

  reduced.addEventListener('change', syncAll);
  document.addEventListener('visibilitychange', syncAll);
  const controls = new MutationObserver(syncAll);
  [document.body, document.documentElement].forEach(element => controls.observe(element, { attributes: true, attributeFilter: ['data-motion-enabled'] }));
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => remove(event.target));
  // The GPU renderer can insert a missing fallback after startup; include it
  // without tying animation playback to the renderer's lifecycle.
  const additions = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) if (node.nodeType === 1) initialize(node);
      for (const node of record.removedNodes) if (node.nodeType === 1 && !node.isConnected) remove(node);
    }
  });
  additions.observe(document.body, { childList: true, subtree: true });

  window.DecorMotionStudio = {
    initialize,
    getDiagnostics: () => ({
      version: '16.0.0', enabled: enabled(), count: states.size,
      images: [...states.values()].map(({ image, animation, visible }) => ({
        section: image.closest('[data-atmosphere]')?.dataset.atmosphere,
        kind: image.classList.contains('studio-atmosphere-fallback') ? 'ribbon' : 'disc',
        visible, running: image.dataset.decorRunning === 'true',
        time: Number((animation.currentTime || 0).toFixed(2)), playState: animation.playState
      }))
    })
  };
  initialize();
})();
